import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { RouterModule } from '@angular/router'

import { Store } from '@ngrx/store'

import { from } from 'rxjs'

import { AiBuilderPlanViewComponent } from '@flowx/designer/modules/ai-builder/components/ai-builder-plan-view/ai-builder-plan-view.component'
import { AiBuilderRailComponent } from '@flowx/designer/modules/ai-builder/components/ai-builder-rail/ai-builder-rail.component'
import {
  AI_BUILDER_RAIL_ANIMATION_MS,
  AiBuilderStore,
} from '@flowx/designer/modules/ai-builder/store/ai-builder.store'
import { FlxGlobalHeaderComponent } from '@flowx/designer/modules/layout/components/global-header/global-header.component'
import { isGlobalHeaderShown } from '@flowx/designer/modules/layout/utils/global-header.util'
import { FunctionNavigationService } from '@flowx/designer/modules/script-builder/components/code-editor/function-navigation.service'
import { KeyboardService } from '@flowx/designer/shared/services/keyboard.service'

import { ReturnTargetService } from './modules/layout/services/return-target.service'
import { MonacoLoaderService } from './modules/monaco-code-editor/monaco-loader.service'
import { AppEffectsService } from './shared/effector/app/app.effects'
import { NavigationErrorHandlerService } from './shared/services/navigation-error-handler.service'
import { UrlParametersStoreService } from './shared/services/url-parameters-store.service'
import {
  selectStableMergedRouteData,
  selectStableUrl,
} from './shared/store/selectors/router.selectors'
import { LayoutOptions } from './shared/types/layout.types'

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    RouterModule,
    AiBuilderRailComponent,
    AiBuilderPlanViewComponent,
    FlxGlobalHeaderComponent,
  ],
})
export class AppComponent {
  #store = inject(Store)
  #appEffectsService = inject(AppEffectsService)
  #monacoLoaderService = inject(MonacoLoaderService)
  #functionNavigationService = inject(FunctionNavigationService)
  #urlParametersStoreService = inject(UrlParametersStoreService)

  #keyboardService = inject(KeyboardService)
  #navigationErrorHandlerService = inject(NavigationErrorHandlerService)
  #returnTargetService = inject(ReturnTargetService)

  aiBuilder = inject(AiBuilderStore)

  url = this.#store.selectSignal(selectStableUrl)
  routeData = this.#store.selectSignal(selectStableMergedRouteData)
  /**
   * Authenticated pages only: those under `/guard`, and the access-denied page, which logs in too so
   * its header can lead somewhere. Login and the login failure have none. A running instance has
   * none either, unless the AI assistant is open: it previews the app with its own toolbar, as
   * before the global header.
   */
  showHeader = computed(() =>
    isGlobalHeaderShown({
      authenticatedPage: this.url()?.startsWith('/guard') || this.url() === '/403',
      isRunningInstance: !!this.routeData()?.[LayoutOptions.IS_RUNNING_INSTANCE],
      assistOn: this.aiBuilder.assistOn(),
      railLeaving: this.aiBuilder.railLeaving(),
    })
  )

  /** The rail's travel, stamped onto the shell as `--ai-rail-animation`. The number lives in the
   * builder's store and nowhere else: it holds the rail mounted for exactly this long while it
   * leaves, so a second copy in the stylesheet could drift and unmount it mid-animation. */
  railAnimationMs = AI_BUILDER_RAIL_ANIMATION_MS

  constructor() {
    this.#urlParametersStoreService.init()
    // Records Go back's return target from the first navigation on.
    this.#returnTargetService.init()

    // ⌘K turns assist on with the maker's conversations over the chat, and off again from any view:
    // once assist is on, openWithConversations declines and the sparkles toggle closes it.
    this.#keyboardService.metaK
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.aiBuilder.openWithConversations() || this.aiBuilder.toggle())

    // One global set of function-navigation providers serves every script
    // editor, Angular and React alike — they share the same window.monaco.
    from(this.#monacoLoaderService.load())
      .pipe(takeUntilDestroyed())
      .subscribe({
        next: () => this.#functionNavigationService.register(),
        error: (error) =>
          console.error('Monaco failed to load; function navigation is unavailable', error),
      })
    this.#navigationErrorHandlerService.init()
  }
}
